import React, { useEffect, useState } from 'react';
import './status.css'; // Import the CSS file

const BatteryStatus = () => {
  const [battery, setBattery] = useState(null);
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    error: null,
  });
  const [memory, setMemory] = useState(null);
  const [hardwareConcurrency, setHardwareConcurrency] = useState(null);
  const [networkInfo, setNetworkInfo] = useState(null);
  const [userAgent, setUserAgent] = useState('');
  const [screenSize, setScreenSize] = useState({});
  const [timezone, setTimezone] = useState('');
  const [storageInfo, setStorageInfo] = useState({
    total: 'Not available',
    used: 'Not available',
    free: 'Not available',
  });
  const [browserLanguage, setBrowserLanguage] = useState('');
  const [platform, setPlatform] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [devicePixelRatio, setDevicePixelRatio] = useState(window.devicePixelRatio);
  const [cookiesEnabled, setCookiesEnabled] = useState(navigator.cookieEnabled ? 'Yes' : 'No');
  const [currentTime, setCurrentTime] = useState(new Date());

  // Get geolocation
  useEffect(() => {
    const handleSuccess = (position) => {
      const { latitude, longitude } = position.coords;
      setLocation({ latitude, longitude, error: null });
    };

    const handleError = (error) => {
      setLocation({ latitude: null, longitude: null, error: error.message });
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError);
    } else {
      setLocation((prev) => ({ ...prev, error: "Geolocation is not supported by this browser." }));
    }
  }, []);

  // Get battery status
  useEffect(() => {
    const getBatteryStatus = async () => {
      if ('getBattery' in navigator) {
        const battery = await navigator.getBattery();
        setBattery(battery);

        const updateBatteryStatus = () => {
          setBattery(battery);
        };

        battery.addEventListener('chargingchange', updateBatteryStatus);
        battery.addEventListener('levelchange', updateBatteryStatus);

        return () => {
          battery.removeEventListener('chargingchange', updateBatteryStatus);
          battery.removeEventListener('levelchange', updateBatteryStatus);
        };
      } else {
        console.error('Battery Status API is not supported on this browser.');
      }
    };

    getBatteryStatus();
  }, []);

  // Get additional system information
  useEffect(() => {
    setMemory(navigator.deviceMemory || 'Not available');
    setHardwareConcurrency(navigator.hardwareConcurrency || 'Not available');
    setNetworkInfo(navigator.connection || 'Not available');
    setUserAgent(navigator.userAgent);
    setScreenSize({
      width: window.screen.width,
      height: window.screen.height,
    });
    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    setBrowserLanguage(navigator.language || navigator.userLanguage);
    setPlatform(navigator.platform);
    
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  // Get storage information (if supported)
  useEffect(() => {
    const getStorageInfo = async () => {
      if (navigator.storage && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate();
        setStorageInfo({
          total: (estimate.quota / (1024 ** 3)).toFixed(2) + ' GB',
          used: (estimate.usage / (1024 ** 3)).toFixed(2) + ' GB',
          free: ((estimate.quota - estimate.usage) / (1024 ** 3)).toFixed(2) + ' GB',
        });
      }
    };

    getStorageInfo();
  }, []);

  // Update current time every second
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  if (!battery) {
    return <div>Loading battery information...</div>;
  }

  return (
    <div className="battery-status-container">
      <h1 className="main-title">System Information Dashboard</h1>

      <div className="card current-time">
        <h2 className="section-title">Current Time</h2>
        <p>{currentTime.toLocaleString()}</p>
      </div>

      <div className="grid-container">
        <div className="card battery-status">
          <h2 className="section-title">Battery Status</h2>
          <p><strong>Charging:</strong> {battery.charging ? 'Yes' : 'No'}</p>
          <p><strong>Battery Level:</strong> {(battery.level * 100).toFixed(0)}%</p>
        </div>

        <div className="card location-info">
          <h2 className="section-title">Geolocation</h2>
          {location.error && <p className="error">{location.error}</p>}
          {location.latitude && location.longitude ? (
            <p>
              <strong>Latitude:</strong> {location.latitude}<br />
              <strong>Longitude:</strong> {location.longitude}
            </p>
          ) : (
            <p>Fetching location...</p>
          )}
        </div>

        <div className="card system-info">
          <h2 className="section-title">System Information</h2>
          <p><strong>Device Memory:</strong> {memory} GB</p>
          <p><strong>Hardware Concurrency:</strong> {hardwareConcurrency} logical processors</p>
        </div>

        {networkInfo && (
          <div className="card network-info">
            <h2 className="section-title">Network Information</h2>
            <p><strong>Type:</strong> {networkInfo.effectiveType || 'Not available'}</p>
            <p><strong>Downlink:</strong> {networkInfo.downlink || 'Not available'} Mbps</p>
            <p><strong>RTT:</strong> {networkInfo.rtt || 'Not available'} ms</p>
          </div>
        )}

        <div className="card user-agent-info">
          <h2 className="section-title">User Agent</h2>
          <p>{userAgent}</p>
        </div>

        <div className="card screen-info">
          <h2 className="section-title">Screen Size</h2>
          <p><strong>Width:</strong> {screenSize.width}px</p>
          <p><strong>Height:</strong> {screenSize.height}px</p>
        </div>

        <div className="card timezone-info">
          <h2 className="section-title">Timezone</h2>
          <p>{timezone}</p>
        </div>

        <div className="card browser-language-info">
          <h2 className="section-title">Browser Language</h2>
          <p>{browserLanguage}</p>
        </div>

        <div className="card platform-info">
          <h2 className="section-title">Platform</h2>
          <p>{platform}</p>
        </div>

        <div className="card online-status-info">
          <h2 className="section-title">Online Status</h2>
          <p>{isOnline ? 'Online' : 'Offline'}</p>
        </div>

        <div className="card device-pixel-ratio-info">
          <h2 className="section-title">Device Pixel Ratio</h2>
          <p>{devicePixelRatio}</p>
        </div>

        <div className="card cookies-enabled-info">
          <h2 className="section-title">Cookies Enabled</h2>
          <p>{cookiesEnabled}</p>
        </div>

        <div className="card storage-info">
          <h2 className="section-title">Storage Information</h2>
          <p><strong>Total Storage:</strong> {storageInfo.total}</p>
          <p><strong>Used Storage:</strong> {storageInfo.used}</p>
          <p><strong>Free Storage:</strong> {storageInfo.free}</p>
        </div>
      </div>
    </div>
  );
};

export default BatteryStatus;
